import type { ApiErrorBody } from './contracts'

/** The backend returns this code for all three ways a session dies. */
export const SESSION_EXPIRED = 'session_expired'

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly detail?: string | null

  constructor(status: number, body: ApiErrorBody) {
    super(body.message)
    this.name = 'ApiError'
    this.status = status
    this.code = body.code
    this.detail = body.detail
  }

  /**
   * Backend restart, cache eviction, or ERP token expiry — indistinguishable here,
   * and handled identically: clear the JWT, show login, leave the outbox alone.
   */
  get isSessionExpired(): boolean {
    return this.status === 401 && this.code === SESSION_EXPIRED
  }
}

/**
 * The device is offline, or the request never reached the server. This is the normal
 * aisle condition, not an error state — the caller routes the operation to the outbox.
 */
export class NetworkError extends Error {
  constructor(cause?: unknown) {
    super('network unreachable')
    this.name = 'NetworkError'
    this.cause = cause
  }
}

/**
 * The server answered an /api call with HTML. With a service worker active this means
 * navigateFallback swallowed the request and served the SPA shell, which must never
 * happen — every API call either reaches the network or goes to the outbox.
 */
export class ShellInterceptedError extends Error {
  readonly path: string

  constructor(path: string) {
    super(`the SPA shell answered ${path} instead of the API`)
    this.name = 'ShellInterceptedError'
    this.path = path
  }
}

export function isOffline(error: unknown): boolean {
  return error instanceof NetworkError
}
